import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Typography,
  useTheme,
  alpha,
} from "@mui/material";
import { Inventory2 } from "@mui/icons-material";
import { useResponsive } from "../../../core/hooks/useResponsive";
import BoughtPositionCard from "./BoughtPositionCard";

const TradeBoughtGrid = ({ boughtList }) => {
  const theme = useTheme();
  const { isCompact } = useResponsive();
  const primary = theme.palette.primary.main;
  const profitC = theme.palette.profitColor ?? "#18b009";
  const lossC = theme.palette.lossColor ?? "#fa2104";
  const rows = Array.isArray(boughtList) ? boughtList : [];

  const numVal = (v) => (v != null && v !== "" ? Number(v) : null);
  const plColor = (pl) => {
    const n = numVal(pl);
    if (n == null) return undefined;
    if (n > 0) return profitC;
    if (n < 0) return lossC;
    return theme.palette.neutralColor ?? "#d5c505";
  };

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <Inventory2 sx={{ color: primary, fontSize: 22 }} />
        <Typography variant="subtitle1" fontWeight={800}>
          Open positions
        </Typography>
        <Typography variant="caption" color="text.secondary">
          ({rows.length})
        </Typography>
      </Box>
      {isCompact ? (
        /* 7 columns need ~500px; a phone has ~344px, so the row becomes a card. */
        rows.length === 0 ? (
          <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
            No positions yet.
          </Typography>
        ) : (
          <Box sx={{ display: "grid", gap: 1, maxHeight: 360, overflowY: "auto", pr: 0.25 }}>
            {rows.map((row, idx) => (
              <BoughtPositionCard key={row.symbol ? `${row.symbol}-${idx}` : idx} row={row} />
            ))}
          </Box>
        )
      ) : (
      <TableContainer
        sx={{
          maxHeight: 320,
          overflow: "auto",
          borderRadius: 2,
          border: `1px solid ${alpha(primary, 0.12)}`,
          "& .MuiTableHead-root .MuiTableCell-root": {
            bgcolor: alpha(primary, 0.07),
            fontWeight: 800,
            fontSize: "0.7rem",
            textTransform: "uppercase",
            letterSpacing: 0.5,
            color: primary,
            borderBottom: `2px solid ${alpha(primary, 0.12)}`,
          },
          "& .MuiTableBody-root .MuiTableRow-root:nth-of-type(even)": {
            bgcolor: alpha(theme.palette.action.hover, 0.04),
          },
          "& .MuiTableBody-root .MuiTableRow-root:hover": {
            bgcolor: alpha(primary, 0.05),
          },
        }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Symbol</TableCell>
              <TableCell align="right">Qty</TableCell>
              <TableCell align="right">Buy</TableCell>
              <TableCell align="right">Invested</TableCell>
              <TableCell align="right">Last</TableCell>
              <TableCell align="right">Value</TableCell>
              <TableCell align="right">P/L</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
                    No positions yet.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, idx) => (
                <TableRow key={row.symbol ? `${row.symbol}-${idx}` : idx} hover>
                  <TableCell sx={{ fontWeight: 700 }}>{row.symbol}</TableCell>
                  <TableCell align="right">{row.qty}</TableCell>
                  <TableCell align="right">{row.buyPrice}</TableCell>
                  <TableCell align="right">{row.invested}</TableCell>
                  <TableCell align="right" sx={{ color: plColor(row.currentPrice) }}>
                    {row.currentPrice}
                  </TableCell>
                  <TableCell align="right" sx={{ color: plColor(row.currentValue) }}>
                    {row.currentValue}
                  </TableCell>
                  <TableCell align="right" sx={{ color: plColor(row.profitLoss), fontWeight: 800 }}>
                    {row.profitLoss}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      )}
    </Box>
  );
};

export default TradeBoughtGrid;
